import React, { useState, useEffect } from 'react';
import { X, Plus, Download, Upload, Edit2, Trash2, Play, LogIn, Cloud } from 'lucide-react';
import {
  listProfiles,
  createProfile,
  deleteProfile,
  renameProfile,
  exportProfile,
  importProfile,
  isUsingFirebase,
} from '../storage/storageService';
import { auth, signInWithPopup, googleProvider, isAuthenticated, isAnonymous, User } from '../firebase';
import { SaveData } from '../storage/localAdapter';
import { useForceSaveOnUnmount } from '../hooks/useAutoSave';
import { vanillaFactions, vanillaVariants, homebrewByFaction, VanillaFaction } from '../data/warbandOptions';
import { FACTIONS } from '../data/premadeTemplates';
import { HOMEBREW_SETS } from '../data/homebrewSets';

interface ProfilesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadProfile: (profileId: string) => void;
  onCreateProfile: (
    profileName: string,
    seedSelection:
      | { type: "custom" }
      | { type: "vanilla"; faction: string; variant?: string }
      | { type: "homebrew"; faction: string; setName: string }
  ) => Promise<string | null>;
  currentProfileId?: string;
  user?: User | null;
  firestoreReady?: boolean;
  currentWarbandData?: SaveData;
}

export const ProfilesModal: React.FC<ProfilesModalProps> = ({
  isOpen,
  onClose,
  onLoadProfile,
  onCreateProfile,
  currentProfileId,
  user,
  firestoreReady = true,
  currentWarbandData
}) => {
  // Force save when modal closes
  useForceSaveOnUnmount();

  const [profiles, setProfiles] = useState<{id: string, name: string, updatedAt: Date, lastAccessedAt: Date}[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [saveAsName, setSaveAsName] = useState('');

  const [mainView, setMainView] = useState<"profiles" | "create">("profiles");

  // Create wizard state
  const [createType, setCreateType] = useState<"vanilla" | "homebrew" | "custom" | null>(null);
  const [selectedFaction, setSelectedFaction] = useState<VanillaFaction | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);
  const [selectedHomebrewFaction, setSelectedHomebrewFaction] = useState<string | null>(null);
  const [selectedHomebrewSet, setSelectedHomebrewSet] = useState<string | null>(null);
  const [createName, setCreateName] = useState<string>("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadProfiles();
    }
  }, [isOpen, firestoreReady]);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const profileList = await listProfiles();
      setProfiles(profileList);
    } catch (error) {
      console.error('Failed to load profiles:', error);
      const errorMessage = (error as Error).message.toLowerCase();
      if (errorMessage.includes('offline') || errorMessage.includes('client is offline') || errorMessage.includes('connecting to cloud')) {
        // Show connection message but don't alert - just show empty state
        setProfiles([]);
      } else {
        // For other errors, show alert
        alert('Failed to load profiles: ' + (error as Error).message);
        setProfiles([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const getImplementedVanillaVariants = (factionName: string): string[] => {
    const faction = FACTIONS[factionName];
    if (!faction) return [];
    return Object.keys(faction.variants || {});
  };

  const hasVanillaPremades = (factionName: string, variantLabel: string): boolean => {
    const faction = FACTIONS[factionName];
    if (!faction) return false;
    const variantKey = variantLabel === factionName ? "Standard" : variantLabel;
    const variant = faction.variants?.[variantKey];
    if (!variant) return false;
    return !!((variant.models?.length || 0) > 0 || (variant.items?.length || 0) > 0);
  };

  const isHomebrewSetImplemented = (setName: string) => {
    return HOMEBREW_SETS.some((s) => s.name === setName);
  };

  const hasHomebrewPremades = (setName: string) => {
    const set = HOMEBREW_SETS.find((s) => s.name === setName);
    if (!set) return false;
    return !!((set.data.modelTemplates?.length || 0) > 0 || (set.data.itemTemplates?.length || 0) > 0);
  };

  const getDisplayVariantLabel = (factionName: string, variantLabel: string) => {
    return variantLabel === factionName ? "Vanilla" : variantLabel;
  };

  const resetCreateWizard = () => {
    setCreateType(null);
    setSelectedFaction(null);
    setSelectedVariant(null);
    setSelectedHomebrewFaction(null);
    setSelectedHomebrewSet(null);
    setCreateName("");
    setCreating(false);
  };

  const handleStartCreate = () => {
    resetCreateWizard();
    setMainView("create");
  };

  const handleConfirmCreate = async () => {
    if (!createName.trim()) return;

    if (!firestoreReady) {
      alert('Please wait — connecting to cloud storage...');
      return;
    }

    let seedSelection:
      | { type: "custom" }
      | { type: "vanilla"; faction: string; variant?: string }
      | { type: "homebrew"; faction: string; setName: string } = { type: "custom" };

    if (createType === "vanilla") {
      if (!selectedFaction) return;
      seedSelection = {
        type: "vanilla",
        faction: selectedFaction,
        variant: selectedVariant || undefined,
      };
    }

    if (createType === "homebrew") {
      if (!selectedHomebrewFaction || !selectedHomebrewSet) return;
      seedSelection = {
        type: "homebrew",
        faction: selectedHomebrewFaction,
        setName: selectedHomebrewSet,
      };
    }

    if (createType === "custom") {
      seedSelection = { type: "custom" };
    }

    try {
      setCreating(true);
      const profileId = await onCreateProfile(createName.trim(), seedSelection);
      if (profileId) {
        await loadProfiles();
        resetCreateWizard();
        setMainView("profiles");
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProfile = async (profileId: string, profileName: string) => {
    if (!confirm(`Are you sure you want to delete "${profileName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteProfile(profileId);
      await loadProfiles();
    } catch (error) {
      console.error('Failed to delete profile:', error);
      alert('Failed to delete profile: ' + (error as Error).message);
    }
  };

  const handleRenameProfile = async (profileId: string) => {
    if (!editingName.trim()) return;

    try {
      await renameProfile(profileId, editingName.trim());
      setEditingProfile(null);
      setEditingName('');
      await loadProfiles();
    } catch (error) {
      console.error('Failed to rename profile:', error);
      alert('Failed to rename profile: ' + (error as Error).message);
    }
  };

  const handleExportProfile = async (profileId: string) => {
    try {
      await exportProfile(profileId);
    } catch (error) {
      console.error('Failed to export profile:', error);
      alert('Failed to export profile: ' + (error as Error).message);
    }
  };

  const handleImportProfile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const profileId = await importProfile(file);
      await loadProfiles();
      onLoadProfile(profileId);
      onClose();
    } catch (error) {
      console.error('Failed to import profile:', error);
      alert('Failed to import profile: ' + (error as Error).message);
    }
  };

  const handleSaveAsProfile = async () => {
    if (!saveAsName.trim() || !currentWarbandData) return;

    if (!firestoreReady) {
      alert('Please wait — connecting to cloud storage...');
      return;
    }

    try {
      console.log(`💾 [ProfilesModal] Saving current warband as: "${saveAsName.trim()}"`);
      const profileId = await createProfile(saveAsName.trim(), currentWarbandData);
      console.log(`✅ [ProfilesModal] Current warband saved as profile successfully: ${profileId}`);
      setSaveAsName('');
      await loadProfiles();
      onLoadProfile(profileId);
      onClose();
    } catch (error) {
      console.error('❌ [ProfilesModal] Failed to save current warband as profile:', error);
      const errorMessage = (error as Error).message.toLowerCase();

      if (errorMessage.includes('permission-denied')) {
        alert('🔒 Permission denied. Please sign in with Google and try again.');
      } else if (errorMessage.includes('offline') || errorMessage.includes('client is offline') || errorMessage.includes('connecting to cloud')) {
        alert('⏳ Please wait — connecting to cloud storage. Try again in a moment.');
      } else if (errorMessage.includes('network') || errorMessage.includes('unavailable')) {
        alert('📶 Connection to cloud storage lost. Please check your internet connection and try again.');
      } else if (errorMessage.includes('requires authentication')) {
        alert('🔐 Authentication required. Please sign in with Google to save profiles.');
      } else {
        alert('❌ Failed to save current warband as profile: ' + (error as Error).message);
      }
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Google sign-in failed:', error);
      alert('Failed to sign in with Google: ' + (error as Error).message);
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-3">
      <div className="ledger-shell max-w-6xl w-full max-h-[90vh] overflow-y-auto text-sm bg-gray-950/95 border border-gray-700 rounded-3xl shadow-[0_30px_90px_rgba(0,0,0,0.65)]">
        {/* Header */}
        <div className="flex flex-col gap-4 justify-between items-start p-5 border-b border-gray-700 md:flex-row md:items-center">
          <div className="flex-1 space-y-2">
            <h2 className="text-lg font-semibold uppercase tracking-wider text-yellow-400">Profile Manager</h2>
            <div className="text-sm text-slate-300">
              {user ? (
                <div className="flex items-center gap-2">
                  {user.isAnonymous ? (
                    <div className="flex items-center gap-2">
                      <span className="text-[#E6E5D3]">Anonymous Account</span>
                      <button
                        onClick={handleGoogleSignIn}
                        className="ledger-btn px-3 py-2 flex items-center gap-2 text-xs uppercase tracking-wider font-semibold"
                      >
                        <LogIn size={12} />
                        Upgrade to Google
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-[#E6E5D3]">{user.displayName || user.email}</span>
                      {isUsingFirebase() && (
                        <span className="bg-[#222] text-[#E6E5D3] px-2 py-1 text-xs font-semibold flex items-center gap-1">
                          <Cloud size={12} />
                          CLOUD SYNC
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-[#E6E5D3]">Not signed in</span>
                  <button
                    onClick={handleGoogleSignIn}
                    className="ledger-btn px-3 py-2 flex items-center gap-2 text-xs uppercase tracking-wider font-semibold"
                  >
                    <LogIn size={12} />
                    Sign In
                  </button>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[#E6E5D3] hover:text-white p-1"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Top-level view switch */}
          <div className="flex flex-wrap gap-2 pb-4 border-b border-gray-700">
            <button
              onClick={() => setMainView("profiles")}
              className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-lg border transition ${
                mainView === "profiles"
                  ? "bg-gray-900/90 border-yellow-700 text-yellow-300"
                  : "bg-gray-900/80 border-gray-700 text-slate-300 hover:bg-gray-900"
              }`}
            >
              Profiles
            </button>
            <button
              onClick={handleStartCreate}
              className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-lg border transition ${
                mainView === "create"
                  ? "bg-gray-900/90 border-yellow-700 text-yellow-300"
                  : "bg-gray-900/80 border-gray-700 text-slate-300 hover:bg-gray-900"
              }`}
            >
              Create new
            </button>
          </div>

          {mainView === "create" && (
            <div className="bg-gray-900/90 border border-gray-700 rounded-3xl p-5 shadow-lg">
              <div className="flex flex-col gap-4 justify-between items-start mb-4 md:flex-row md:items-center">
                <h3 className="text-base font-semibold uppercase tracking-wider text-yellow-300">Create new warband</h3>
                <button
                  onClick={() => {
                    resetCreateWizard();
                    setMainView("profiles");
                  }}
                  className="text-xs text-slate-400 hover:text-slate-100 underline"
                >
                  Back to profiles
                </button>
              </div>

              {!firestoreReady && (
                    <div className="text-xs mb-3 flex items-center gap-2 text-slate-400">
                      <div className="animate-spin h-4 w-4 border-b-2 border-slate-400"></div>
                      Connecting to cloud storage...
                    </div>
                  )}

                  {/* Step 1: Choose type */}
                  {!createType && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <button
                        onClick={() => setCreateType("vanilla")}
                        className="bg-gray-900/80 border border-gray-700 hover:bg-gray-800 text-slate-100 p-4 text-left rounded-3xl shadow-sm transition"
                      >
                        <div className="text-sm font-semibold uppercase tracking-wider mb-1 text-yellow-300">Vanilla</div>
                        <div className="text-xs text-slate-400">Start with official premades (faction + variant).</div>
                      </button>
                      <button
                        onClick={() => setCreateType("homebrew")}
                        className="bg-gray-900/80 border border-gray-700 hover:bg-gray-800 text-slate-100 p-4 text-left rounded-3xl shadow-sm transition"
                      >
                        <div className="text-sm font-semibold uppercase tracking-wider mb-1 text-yellow-300">Homebrew</div>
                        <div className="text-xs text-slate-400">Start with a community pack.</div>
                      </button>
                      <button
                        onClick={() => setCreateType("custom")}
                        className="bg-gray-900/80 border border-gray-700 hover:bg-gray-800 text-slate-100 p-4 text-left rounded-3xl shadow-sm transition"
                      >
                        <div className="text-sm font-semibold uppercase tracking-wider mb-1 text-yellow-300">Blank</div>
                        <div className="text-xs text-slate-400">Blank profile (no premades).</div>
                      </button>
                    </div>
                  )}

              {/* Step 2a: Vanilla faction */}
              {createType === "vanilla" && !selectedFaction && (
                <div className="mt-4">
                  <div className="text-gray-300 font-semibold mb-2">Choose a faction</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {vanillaFactions.map((f) => (
                      <button
                        key={f}
                        onClick={() => setSelectedFaction(f)}
                        className="bg-gray-800/60 hover:bg-gray-800 border border-gray-700 rounded p-4 text-left"
                      >
                        <div className="text-white font-bold uppercase tracking-wider">{f}</div>
                        <div className="text-xs text-gray-400 mt-1">
                          {getImplementedVanillaVariants(f).length > 0
                            ? `${getImplementedVanillaVariants(f).length} variant(s) implemented`
                            : "Variants partially/Not yet implemented"}
                        </div>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setCreateType(null)}
                    className="mt-4 text-gray-400 hover:text-gray-200 text-sm underline"
                  >
                    Change type
                  </button>
                </div>
              )}

              {/* Step 2b: Vanilla variant */}
              {createType === "vanilla" && selectedFaction && !selectedVariant && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-white font-semibold">Choose a subfaction or Vanilla</div>
                    <button
                      onClick={() => setSelectedFaction(null)}
                      className="text-slate-300 hover:text-white text-sm underline"
                    >
                      Back
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {(vanillaVariants[selectedFaction] || []).map((variantLabel) => {
                      const implementedKeys = getImplementedVanillaVariants(selectedFaction);
                      const variantKey = variantLabel === selectedFaction ? "Standard" : variantLabel;
                      const exists = implementedKeys.includes(variantKey);
                      const hasData = exists && hasVanillaPremades(selectedFaction, variantLabel);
                      const displayLabel = getDisplayVariantLabel(selectedFaction, variantLabel);

                      return (
                        <button
                          key={variantLabel}
                          onClick={() => setSelectedVariant(variantLabel)}
                          className="bg-slate-950 hover:bg-slate-900 border border-slate-700 rounded p-4 text-left"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-white font-bold">{displayLabel}</div>
                            {!hasData && (
                              <span className="text-xs bg-slate-800 text-slate-300 px-2 py-1 rounded border border-slate-700">
                                Not implemented
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-400 mt-1">
                            {hasData
                              ? "Seeds this variant’s premades"
                              : "This variant is not implemented yet — no premades loaded. Add your own in Templates later!"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 2c: Homebrew */}
              {createType === "homebrew" && !selectedHomebrewSet && (
                <div className="mt-4 space-y-5">
                  {Object.entries(homebrewByFaction).map(([factionName, sets]) => (
                    <div key={factionName}>
                      <div className="text-gray-300 font-semibold mb-2">{factionName}</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {sets.map((setName) => {
                          const implemented = isHomebrewSetImplemented(setName);
                          const hasData = hasHomebrewPremades(setName);
                          return (
                            <button
                              key={setName}
                              onClick={() => {
                                setSelectedHomebrewFaction(factionName);
                                setSelectedHomebrewSet(setName);
                              }}
                              className="bg-gray-800/60 hover:bg-gray-800 border border-gray-700 rounded p-4 text-left"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="text-white font-bold">{setName}</div>
                                {!implemented || !hasData && (
                                  <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded border border-gray-600">
                                    Not implemented
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                {implemented && hasData
                                  ? "Seeds this homebrew pack’s templates"
                                  : "This homebrew pack is not implemented yet — no premades loaded. Add your own in Templates later!"}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  <div className="text-xs text-gray-400 pt-2 border-t border-gray-700">
                    Want your homebrew added to the list (even if small/no following)? Just shoot me a message!
                  </div>

                  <button
                    onClick={() => setCreateType(null)}
                    className="text-gray-400 hover:text-gray-200 text-sm underline"
                  >
                    Change type
                  </button>
                </div>
              )}

              {/* Step 3: Name + Create */}
              {(createType === "custom" || (createType === "vanilla" && selectedVariant) || (createType === "homebrew" && selectedHomebrewSet)) && (
                <div className="mt-5 border-t border-slate-700 pt-4">
                  <div className="text-slate-200 font-semibold mb-2">Profile name</div>
                  <input
                    type="text"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    placeholder="Enter profile name..."
                    className="w-full bg-slate-950 text-slate-200 px-3 py-2 rounded border border-slate-700 focus:border-red-800 focus:outline-none"
                    disabled={!firestoreReady || creating}
                  />

                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mt-3">
                    <div className="text-xs text-slate-400">
                      {createType === "vanilla" && selectedFaction && selectedVariant && (
                        <>Seeding: Vanilla • {selectedFaction} • {getDisplayVariantLabel(selectedFaction, selectedVariant)}</>
                      )}
                      {createType === "homebrew" && selectedHomebrewFaction && selectedHomebrewSet && (
                        <>Seeding: Homebrew • {selectedHomebrewFaction} • {selectedHomebrewSet}</>
                      )}
                      {createType === "custom" && <>Seeding: Blank profile</>}
                    </div>
                    <button
                      onClick={handleConfirmCreate}
                      disabled={
                        creating ||
                        !firestoreReady ||
                        !createName.trim() ||
                        (createType === "vanilla" && !selectedVariant) ||
                        (createType === "homebrew" && !selectedHomebrewSet)
                      }
                      className="bg-red-900 hover:bg-red-800 disabled:bg-slate-700 text-white font-semibold px-4 py-2 rounded border border-red-800 flex items-center gap-2 uppercase tracking-wider disabled:cursor-not-allowed"
                    >
                      <Plus size={16} />
                      {creating ? "CREATING..." : "CREATE"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Profiles view */}
          {mainView === "profiles" && (
            <div className="space-y-4">
              {currentWarbandData && (
                  <div className="bg-gray-900/90 rounded-3xl border border-gray-700 p-4 shadow-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-bold text-green-300 uppercase tracking-wide">Save current warband</h3>
                    </div>
                    {!firestoreReady && (
                      <div className="text-yellow-500 text-sm mb-3 flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-500"></div>
                        Connecting to cloud storage...
                      </div>
                    )}
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <input
                        type="text"
                        value={saveAsName}
                        onChange={(e) => setSaveAsName(e.target.value)}
                        placeholder="Enter profile name..."
                        className="flex-1 bg-gray-900/90 text-slate-200 px-3 py-2 rounded border border-gray-700 focus:border-green-800 focus:outline-none"
                        onKeyPress={(e) => e.key === 'Enter' && handleSaveAsProfile()}
                        disabled={!firestoreReady}
                      />
                      <button
                        onClick={handleSaveAsProfile}
                        disabled={!saveAsName.trim() || !firestoreReady}
                        className="bg-green-900 hover:bg-green-800 disabled:bg-gray-700 text-white font-semibold px-4 py-2 rounded border border-green-800 flex items-center justify-center gap-2 uppercase tracking-wider disabled:cursor-not-allowed"
                      >
                        <Download size={16} />
                        {firestoreReady ? 'SAVE AS' : 'WAITING...'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="bg-gray-900/90 rounded-3xl border border-gray-700 p-4 shadow-lg">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
                    <h3 className="text-base font-bold text-red-400 uppercase tracking-wide">Existing profiles</h3>
                    <label className="bg-red-900 hover:bg-red-800 text-white font-semibold px-3 py-2 rounded border border-red-800 flex items-center gap-2 uppercase tracking-wider cursor-pointer">
                      <Upload size={14} />
                      IMPORT
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleImportProfile}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {loading ? (
                    <div className="text-center py-8 text-slate-400">Loading profiles...</div>
                  ) : profiles.length === 0 ? (
                    <div className="text-center py-8 text-slate-400 uppercase tracking-wide">
                      No profiles found. Create your first profile above.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {profiles.map((profile) => (
                        <div
                          key={profile.id}
                          className="bg-gray-900/80 rounded-3xl border border-gray-700 p-4"
                        >
                          <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-start">
                            <div className="flex-1">
                              {editingProfile === profile.id ? (
                                <div className="flex flex-col gap-2 md:flex-row">
                                  <input
                                    type="text"
                                    value={editingName}
                                    onChange={(e) => setEditingName(e.target.value)}
                                    className="flex-1 bg-gray-900/90 text-slate-200 px-3 py-2 rounded border border-gray-700 focus:border-red-800 focus:outline-none text-sm"
                                    onKeyPress={(e) => e.key === 'Enter' && handleRenameProfile(profile.id)}
                                    autoFocus
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleRenameProfile(profile.id)}
                                      className="bg-green-900 hover:bg-green-800 text-white px-3 py-2 rounded text-sm"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingProfile(null);
                                        setEditingName('');
                                      }}
                                      className="bg-gray-800 hover:bg-gray-700 text-slate-200 px-3 py-2 rounded text-sm"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <h4 className="text-lg font-bold text-yellow-500 uppercase tracking-wider">{profile.name}</h4>
                                  <div className="text-xs text-slate-400 mt-1">Last accessed: {formatDate(profile.lastAccessedAt)}</div>
                                  <div className="text-xs text-slate-500">Updated: {formatDate(profile.updatedAt)}</div>
                                </div>
                              )}
                            </div>

                            <div className="flex flex-wrap gap-2">
                              {currentProfileId === profile.id && (
                                <span className="bg-green-900/50 text-green-400 px-2 py-1 rounded text-xs font-bold border border-green-800 uppercase tracking-wider">
                                  CURRENT
                                </span>
                              )}

                              <button
                                onClick={() => onLoadProfile(profile.id)}
                                className="bg-red-900 hover:bg-red-800 text-white px-3 py-2 rounded text-sm font-semibold border border-red-800 flex items-center gap-2 uppercase tracking-wider"
                              >
                                <Play size={14} />
                                LOAD
                              </button>

                              <button
                                onClick={() => setEditingProfile(profile.id)}
                                className="bg-gray-800 hover:bg-gray-700 text-slate-300 px-3 py-2 rounded text-sm flex items-center gap-2"
                              >
                                <Edit2 size={14} />
                                Edit
                              </button>

                              <button
                                onClick={() => handleExportProfile(profile.id)}
                                className="bg-gray-800 hover:bg-gray-700 text-slate-300 px-3 py-2 rounded text-sm flex items-center gap-2"
                              >
                                <Download size={14} />
                                Export
                              </button>

                              <button
                                onClick={() => handleDeleteProfile(profile.id, profile.name)}
                                className="bg-red-900 hover:bg-red-800 text-white px-3 py-2 rounded text-sm flex items-center gap-2 border border-red-800"
                              >
                                <Trash2 size={14} />
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
          )}
        </div>
      </div>
    </div>
  );
};
